// Freehand gallery drawer — saves & loads freehand designs by hitting the
// existing /api/designs endpoints. Freehand-specific panel data (per-panel
// points, colours, smooth flags, symmetry group + transforms) rides along in
// the `freehand_panels` field; `vertices/faces/colors` are also filled in so
// the design can round-trip through legacy tools.

import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, FolderOpen } from 'lucide-react';
import { useFreehand } from '@/lib/freehandStore';
import { useAuth } from '@/lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FreehandGalleryDrawer({ open, onOpenChange }) {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadDesign = useFreehand((s) => s.loadDesign);
  const { user } = useAuth();

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/designs`);
      // Show only freehand designs in this gallery to keep it focused.
      setDesigns(data.filter((d) => d.template === 'freehand'));
    } catch {
      toast.error('Could not load gallery');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) fetchList(); }, [open]);

  const handleLoad = async (id) => {
    try {
      const { data } = await axios.get(`${API}/designs/${id}`);
      loadDesign(data);
      toast.success(`Loaded "${data.name}"`);
      onOpenChange(false);
    } catch {
      toast.error('Load failed');
    }
  };
  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/designs/${id}`);
      setDesigns((d) => d.filter((x) => x.id !== id));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col" data-testid="freehand-gallery-drawer">
        <SheetHeader>
          <SheetTitle className="tracking-tight">Freehand Designs</SheetTitle>
          <SheetDescription>
            {user
              ? 'Load a previous freehand design back into the workspace.'
              : 'Sign in to load and save freehand designs.'}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-3 overflow-y-auto pr-1 flex-1 min-h-0" data-testid="freehand-gallery-list">
          {loading && <div className="text-sm text-slate-500">Loading…</div>}
          {!loading && designs.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center">
              <FolderOpen className="w-8 h-8 text-slate-400 mx-auto mb-3" />
              <div className="text-sm text-slate-600 font-medium">No saved freehand designs yet</div>
              <div className="text-xs text-slate-400 mt-1">Draw something, then hit "Save" in the topbar.</div>
            </div>
          )}
          {designs.map((d) => (
            <button
              key={d.id}
              onClick={() => handleLoad(d.id)}
              data-testid={`freehand-gallery-card-${d.id}`}
              className="w-full flex items-center gap-3 rounded-2xl border border-slate-200 hover:border-slate-300 bg-white p-3 text-left transition-colors duration-150"
            >
              {d.thumbnail ? (
                <img src={d.thumbnail} alt="" className="w-14 h-14 rounded-xl object-cover bg-slate-100" />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 text-xs">
                  no img
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900 truncate">{d.name}</div>
                <div className="text-xs text-slate-500 truncate">
                  freehand · {d.panel_count} panel{d.panel_count === 1 ? '' : 's'}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => handleDelete(d.id, e)}
                className="text-slate-400 hover:text-red-500 rounded-full"
                data-testid={`freehand-btn-delete-${d.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
